/**
 * The GlitchAds-owned hosts a generated landing page serves on, and the
 * same-domain exception they earn (#6480). Mirrors the backend single source of
 * truth `backend/app/landing_pages/hosts.py`; the allowlist itself lives in
 * `consts.ts`. Used by the ad-URL flow so a Final URL pointing at a generated
 * landing page is not rejected as cross-domain by the same-domain rule (#5548).
 */
import { LANDING_PAGE_HOSTS } from '@/lib/consts';

// Multi-part public suffixes whose registrable domain is the last three labels.
const MULTI_PART_TLDS = new Set([
  'co.uk', 'com.au', 'co.nz', 'co.jp', 'com.br', 'co.in',
  'org.uk', 'net.au', 'co.za', 'com.mx', 'co.kr', 'com.sg',
  'com.hk', 'co.il', 'com.ar', 'com.tw', 'co.th', 'com.my',
]);

const hostOf = (url: string): string | null => {
  const trimmed = url.trim();
  if (!trimmed) return null;
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    return new URL(withScheme).hostname || null;
  } catch {
    return null;
  }
};

const getBaseDomain = (hostname: string): string => {
  const withoutWww = hostname.toLowerCase().replace(/^www\./, '');
  const parts = withoutWww.split('.');
  if (parts.length >= 3 && MULTI_PART_TLDS.has(parts.slice(-2).join('.'))) {
    return parts.slice(-3).join('.');
  }
  return parts.length >= 2 ? parts.slice(-2).join('.') : withoutWww;
};

/**
 * Whether `url`'s host is a GlitchAds landing-page serving host. Host match only:
 * the path is irrelevant, and a look-alike such as `pages.glitchads.ai.evil.com`
 * is rejected. Scheme optional. Mirrors backend `is_landing_page_host`.
 */
export const isLandingPageHost = (url: string): boolean => {
  const host = hostOf(url);
  return host !== null && LANDING_PAGE_HOSTS.has(host);
};

/**
 * Whether `nextUrl` is allowed on an ad whose reference URL is `refUrl`: true when
 * both share a registrable base domain, OR when `nextUrl` is a GlitchAds
 * landing-page host (the #5548 same-domain exception). Scheme optional; returns
 * false on unparseable input.
 */
export const isSameHostOrGeneratedLp = (nextUrl: string, refUrl: string): boolean => {
  if (isLandingPageHost(nextUrl)) return true;
  const nextHost = hostOf(nextUrl);
  const refHost = hostOf(refUrl);
  if (nextHost === null || refHost === null) return false;
  return getBaseDomain(nextHost) === getBaseDomain(refHost);
};
